import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/http/api-error';

import { useMutarPlanes } from '../planes.api';
import type { Plan } from '../planes.types';

interface PlanDialogoProps {
  /** Plan a editar; `null` para crear uno nuevo. */
  plan: Plan | null;
  onClose: () => void;
}

const MAX_NOMBRE = 100;
const MAX_CODIGO = 30;

/** Paleta del panel anterior para el color del plan. */
const COLORES = [
  '#6c757d',
  '#0d6efd',
  '#28a745',
  '#ffc107',
  '#dc3545',
  '#17a2b8',
  '#6f42c1',
  '#fd7e14',
];

/** Mensaje de error legible para las mutaciones. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo guardar el plan.';
}

/**
 * Alta y edición de un plan.
 *
 * Igual que el panel anterior: al crear se piden nombre, código (en mayúsculas),
 * descripción, precio y color; al editar el **código no se toca** (el formulario
 * del panel anterior lo mostraba deshabilitado) y se puede activar o desactivar.
 */
export function PlanDialogo({ plan, onClose }: PlanDialogoProps) {
  const mutaciones = useMutarPlanes();

  const [nombre, setNombre] = useState(plan?.nombre ?? '');
  const [codigo, setCodigo] = useState(plan?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(plan?.descripcion ?? '');
  const [precio, setPrecio] = useState(plan ? String(plan.precio_mensual) : '0');
  const [precioAnual, setPrecioAnual] = useState(plan ? String(plan.precio_anual) : '0');
  const [maxUsuarios, setMaxUsuarios] = useState(plan ? String(plan.max_usuarios) : '0');
  const [maxTerminales, setMaxTerminales] = useState(plan ? String(plan.max_terminales) : '0');
  const [color, setColor] = useState(plan?.color ?? '#6c757d');
  const [activo, setActivo] = useState(plan?.activo ?? true);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const guardando = mutaciones.crear.isPending || mutaciones.actualizar.isPending;

  const enviar = () => {
    const encontrados: Record<string, string> = {};
    const valorPrecio = Number(precio);

    if (!nombre.trim()) encontrados.nombre = 'El nombre es obligatorio.';
    else if (nombre.trim().length > MAX_NOMBRE) encontrados.nombre = `Máximo ${MAX_NOMBRE} caracteres.`;

    if (!plan) {
      if (!codigo.trim()) encontrados.codigo = 'El código es obligatorio.';
      else if (codigo.trim().length > MAX_CODIGO) encontrados.codigo = `Máximo ${MAX_CODIGO} caracteres.`;
    }

    if (!Number.isFinite(valorPrecio) || valorPrecio < 0) {
      encontrados.precio = 'Indica un precio válido (no negativo).';
    }

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) return;

    const comunes = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      precio_mensual: Number(valorPrecio.toFixed(2)),
      precio_anual: Number(Number(precioAnual || 0).toFixed(2)),
      max_usuarios: Math.max(0, Math.trunc(Number(maxUsuarios) || 0)),
      max_terminales: Math.max(0, Math.trunc(Number(maxTerminales) || 0)),
      color,
      activo,
    };

    const alFallar = (error: unknown) => setAviso(mensajeError(error));

    if (plan) {
      mutaciones.actualizar.mutate({ id: plan.id, payload: comunes }, { onSuccess: onClose, onError: alFallar });
      return;
    }

    mutaciones.crear.mutate(
      { ...comunes, codigo: codigo.trim().toUpperCase() },
      { onSuccess: onClose, onError: alFallar },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={plan ? `Editar ${plan.nombre}` : 'Nuevo plan'}
      description={
        plan
          ? 'El código del plan no se puede cambiar una vez creado.'
          : 'Los planes definen qué módulos puede usar la estación.'
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={enviar} loading={guardando}>
            {plan ? 'Guardar cambios' : 'Crear plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="No se pudo guardar">
            {aviso}
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nombre del plan"
            value={nombre}
            maxLength={MAX_NOMBRE}
            error={errores.nombre}
            onChange={(event) => setNombre(event.target.value)}
            placeholder="Ej. Plan Empresarial"
          />

          <Input
            label="Código"
            value={codigo}
            maxLength={MAX_CODIGO}
            disabled={Boolean(plan)}
            error={errores.codigo}
            hint={plan ? 'No editable.' : 'En mayúsculas, sin repetirse.'}
            onChange={(event) => setCodigo(event.target.value.toUpperCase())}
            placeholder="Ej. EMPRESARIAL"
          />
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-700">Descripción</span>
          <textarea
            value={descripcion}
            rows={2}
            onChange={(event) => setDescripcion(event.target.value)}
            placeholder="Descripción del plan (opcional)."
            className="w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-4">
          <Input
            label="Precio mensual"
            type="number"
            step="0.01"
            min="0"
            value={precio}
            error={errores.precio}
            onChange={(event) => setPrecio(event.target.value)}
          />
          <Input
            label="Precio anual"
            type="number"
            step="0.01"
            min="0"
            value={precioAnual}
            onChange={(event) => setPrecioAnual(event.target.value)}
          />
          <Input
            label="Máx. usuarios"
            type="number"
            min="0"
            value={maxUsuarios}
            hint="0 = ilimitados"
            onChange={(event) => setMaxUsuarios(event.target.value)}
          />
          <Input
            label="Máx. terminales"
            type="number"
            min="0"
            value={maxTerminales}
            hint="0 = ilimitados"
            onChange={(event) => setMaxTerminales(event.target.value)}
          />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-ink-700">Color</span>
            <div className="flex flex-wrap gap-1.5">
              {COLORES.map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  aria-label={`Color ${opcion}`}
                  aria-pressed={color === opcion}
                  onClick={() => setColor(opcion)}
                  style={{ backgroundColor: opcion }}
                  className={
                    color === opcion
                      ? 'size-6 rounded-full ring-2 ring-ink-900 ring-offset-2'
                      : 'size-6 rounded-full ring-1 ring-ink-200'
                  }
                />
              ))}
            </div>
          </div>

          {plan ? (
            <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) => setActivo(event.target.checked)}
                className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
              />
              Plan activo
            </label>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
